import type { RefObject, LegacyRef } from 'react'
import { FolderOpen, ChevronDown, Check } from 'lucide-react'
import clsx from 'clsx'

export interface PortfolioDropdownProps {
  portfolios: Array<{ id: string; name: string; description?: string | null }>
  selectedId: string
  show: boolean
  onSelect: (id: string) => void
  onToggle: () => void
  menuRef: RefObject<HTMLDivElement | null>
}

export function PortfolioDropdown({
  portfolios,
  selectedId,
  show,
  onSelect,
  onToggle,
  menuRef,
}: PortfolioDropdownProps) {
  return (
    <div className="relative" ref={menuRef as LegacyRef<HTMLDivElement>}>
      <button
        onClick={onToggle}
        className="portfolio-button inline-flex h-7 items-center gap-1.5 rounded-md border bg-card px-2.5 text-xs font-medium text-foreground/80 transition hover:bg-foreground/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <FolderOpen className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Portfolio</span>
        <span className="max-w-[140px] truncate text-foreground">
          {selectedId
            ? portfolios.find((p) => p.id === selectedId)?.name || 'Selected'
            : 'All Knowledge'}
        </span>
        <ChevronDown
          className={clsx(
            'h-3.5 w-3.5 text-muted-foreground transition-transform',
            show && 'rotate-180',
          )}
        />
      </button>

      {show && (
        <div className="portfolio-dropdown absolute left-0 right-0 mt-2 rounded-lg border border-subtle bg-card py-1 shadow-lg z-50">
          <button
            onClick={() => onSelect('')}
            className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left text-sm transition hover:bg-foreground/[0.04]"
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium text-foreground">All Knowledge</div>
              <div className="mt-0.5 text-xs text-muted-foreground">Search across every portfolio</div>
            </div>
            {!selectedId && <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />}
          </button>

          {portfolios.length > 0 && <div className="my-1 border-t border-subtle" />}

          <div className="max-h-72 overflow-y-auto">
            {portfolios.map((portfolio) => (
              <button
                key={portfolio.id}
                onClick={() => onSelect(portfolio.id)}
                className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left text-sm transition hover:bg-foreground/[0.04]"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-foreground">{portfolio.name}</div>
                  {portfolio.description && (
                    <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                      {portfolio.description}
                    </div>
                  )}
                </div>
                {selectedId === portfolio.id && (
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                )}
              </button>
            ))}
          </div>

          {portfolios.length === 0 && (
            <div className="px-3 py-4 text-center">
              <p className="text-xs text-muted-foreground">No portfolios yet</p>
              <p className="mt-0.5 text-xs text-subtle-foreground">Create one in Knowledge Hub</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
